import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createQuoteRequest, buildQuoteDescription, errorMessage } from "@/lib/drivego";
import HoneypotField from "@/components/HoneypotField";
import { sortServicesCustom } from "@/utils/sortServices";
import { toast } from "sonner";
import { CheckCircle, Loader2 } from "lucide-react";

const EMPTY = {
  customer_name: "",
  license_plate: "",
  mileage: "",
  phone: "",
  email: "",
  comments: "",
  postal_code: "",
  website: "", // honeypot
};

const inputCls = "mt-1 bg-white/[0.05] border-white/10 text-white placeholder:text-[#52525B] rounded-xl focus:border-[#00F0FF]";

/**
 * Shared "Få tilbud" dialog used by the hero booking card and the quote widget.
 * If no `service` is pre-selected but `allServices` is provided, a service
 * picker is shown so the dialog can also work as a general top-of-page CTA.
 * If no `postalCode` is pre-collected by the caller, a postal code field is
 * shown inside the dialog instead.
 */
const QuoteRequestDialog = ({ open, onOpenChange, service, postalCode, allServices = [] }) => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [pickedServiceId, setPickedServiceId] = useState("");

  const effectiveService = service || allServices.find((s) => s.id === pickedServiceId);
  const needsServicePicker = !service;
  const needsPostalCode = !postalCode;

  const handleFormChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSuccess(false);
      setForm(EMPTY);
      setPickedServiceId("");
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (needsServicePicker && !pickedServiceId) {
      toast.error("Vælg venligst en tjeneste");
      return;
    }
    setSubmitting(true);
    try {
      const postal = postalCode || form.postal_code;
      await createQuoteRequest({
        description: buildQuoteDescription([
          ["Tjeneste", effectiveService?.name],
          ["Postnummer", postal],
          ["Kilometerstand", form.mileage],
          ["Kommentar", form.comments],
        ]),
        customer: {
          name: form.customer_name,
          phone: form.phone,
          email: form.email,
          postalCode: postal,
        },
        licensePlate: form.license_plate,
        website: form.website,
      });
      setSuccess(true);
      toast.success("Din forespørgsel er sendt!");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && (o ? onOpenChange(true) : close())}>
      <DialogContent
        className="public-site bg-[#0A0A0A] border border-white/10 text-white rounded-xl max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="quote-dialog"
      >
        {success ? (
          <div className="py-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-[#34C759]/20 rounded-full">
              <CheckCircle className="w-10 h-10 text-[#34C759]" />
            </div>
            <h2 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "Saira" }}>
              Tak for din forespørgsel
            </h2>
            <p className="text-[#A0AEC0] mb-6">
              Vi har modtaget din anmodning og vender tilbage inden for 2 timer med et personligt tilbud.
            </p>
            <Button
              onClick={close}
              className="bg-[#00F0FF] hover:bg-[#00D0E0] text-black font-bold rounded-full px-8 glow-cyan"
              data-testid="close-quote-success-btn"
            >
              Luk
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-black text-white" style={{ fontFamily: "Saira" }}>
                Få et tilbud
              </DialogTitle>
              <DialogDescription className="text-[#A0AEC0]">
                {effectiveService?.name || "Vælg en tjeneste nedenfor"}
                {postalCode ? ` · Postnr. ${postalCode}` : ""}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 relative">
              <HoneypotField value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
              {needsServicePicker && (
                <div>
                  <Label className="text-white">Tjeneste *</Label>
                  <Select value={pickedServiceId} onValueChange={setPickedServiceId}>
                    <SelectTrigger className={inputCls} data-testid="quote-service-select">
                      <SelectValue placeholder="Vælg en tjeneste" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 text-white max-h-72">
                      {sortServicesCustom(allServices).map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`quote-service-option-${s.id}`}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-white">Navn *</Label>
                <Input name="customer_name" value={form.customer_name} onChange={handleFormChange} required placeholder="Anders Jensen" className={inputCls} data-testid="quote-name-input" />
              </div>
              {needsPostalCode && (
                <div>
                  <Label className="text-white">Postnummer *</Label>
                  <Input name="postal_code" value={form.postal_code} onChange={handleFormChange} required placeholder="8000" maxLength={4} className={inputCls} data-testid="quote-postal-input" />
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Nummerplade *</Label>
                  <Input name="license_plate" value={form.license_plate} onChange={handleFormChange} required placeholder="AB 12 345" className={inputCls} data-testid="quote-license-input" />
                </div>
                <div>
                  <Label className="text-white">Kilometerstand *</Label>
                  <Input name="mileage" value={form.mileage} onChange={handleFormChange} required placeholder="120.000" className={inputCls} data-testid="quote-mileage-input" />
                </div>
              </div>
              <div>
                <Label className="text-white">Telefonnummer *</Label>
                <Input name="phone" value={form.phone} onChange={handleFormChange} required placeholder="+45 12 34 56 78" className={inputCls} data-testid="quote-phone-input" />
              </div>
              <div>
                <Label className="text-white">Email *</Label>
                <Input name="email" type="email" value={form.email} onChange={handleFormChange} required placeholder="anders@example.com" className={inputCls} data-testid="quote-email-input" />
              </div>
              <div>
                <Label className="text-white">Evt. Kommentarer</Label>
                <Textarea name="comments" value={form.comments} onChange={handleFormChange} placeholder="Beskriv gerne problemet eller særlige ønsker..." className={`${inputCls} min-h-[80px]`} data-testid="quote-comments-input" />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={close} disabled={submitting} className="flex-1 border-white/15 bg-transparent text-white hover:bg-white/10 hover:text-white rounded-xl min-h-12" data-testid="quote-cancel-btn">
                  Annuller
                </Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-[#00F0FF] hover:bg-[#00D0E0] text-black font-bold rounded-full glow-cyan" data-testid="submit-quote-btn">
                  {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sender...</>) : "Få tilbud"}
                </Button>
              </div>
              <p className="text-[11px] text-[#A0AEC0] text-center mt-3">
                Ved at sende accepterer du vores{" "}
                <a href="/privatlivspolitik" target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline" data-testid="quote-privacy-link">
                  privatlivspolitik
                </a>
                .
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteRequestDialog;
